import {
  Avatar,
  Box,
  Card,
  CardContent,
  CardHeader,
  Divider,
  Grid,
  Typography,
} from "@mui/material";
import { MessageSquare, Pen } from "lucide-react";
import React from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { User } from "../../../../../../models/user";
import { useAuth } from "../../../../../Reusable/AuthProvider";
import ButtonPrimary from "../../../../../Reusable/ButtonPrimary";
import { useChat } from "../../../../../Reusable/UseUserChat";

interface UserPageProps {
  user: User | null;
}

const UserPage: React.FC<UserPageProps> = ({ user }) => {
  const { user: userConnected } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { openChat } = useChat();

  if (!user) {
    return <Typography>{t("USER_PAGE_LOADING")}</Typography>;
  }

  return (
    <Box
      sx={{
        maxWidth: 800,
        mx: "auto",
        py: 4,
        px: 2,
        backgroundColor: "hsl(var(--background))",
      }}
    >
      {/* Profile Header */}
      <Grid container spacing={2} alignItems="center">
        <Grid item>
          <Avatar
            src={user.imageUrl || undefined}
            alt={`${user.firstName} ${user.lastName}`}
            sx={{ width: 80, height: 80 }}
          />
        </Grid>
        <Grid item xs>
          <Typography
            variant="h5"
            fontWeight="bold"
            sx={{ color: "hsl(var(--foreground))" }}
          >
            {user.title} {user.firstName} {user.lastName}
          </Typography>
          <Typography
            variant="body2"
            sx={{ color: "hsl(var(--muted-foreground))" }}
          >
            {user.email}
          </Typography>
          <Typography
            variant="body2"
            sx={{ color: "hsl(var(--muted-foreground))" }}
          >
            {user.phoneNumber}
          </Typography>
          <Typography
            variant="body2"
            sx={{ color: "hsl(var(--muted-foreground))" }}
          >
            {user.entity?.name}
          </Typography>
          <Typography
            variant="body2"
            sx={{ color: "hsl(var(--muted-foreground))" }}
          >
            {t("USER_PAGE_LOCATION_LABEL")}: {user.location?.name}
          </Typography>
        </Grid>
        <Grid item>
          <ButtonPrimary
            variant="contained"
            startIcon={
              user.id === userConnected?.id ? <Pen /> : <MessageSquare />
            }
            onClick={() => {
              if (user.id === userConnected?.id)
                navigate(`/feature/profile/edit`);
              else openChat([user.id, userConnected?.id].sort().join("_"));
            }}
            sx={{ textTransform: "none" }}
          >
            {user.id === userConnected?.id
              ? t("USER_PAGE_EDIT_PROFILE")
              : t("USER_PAGE_SEND_MESSAGE")}
          </ButtonPrimary>
        </Grid>
      </Grid>

      <Divider
        sx={{
          my: 3,
          borderColor: "hsl(var(--border))",
        }}
      />

      {/* Basic Info Card */}
      <Card
        sx={{
          boxShadow: "none",
          border: "1px solid hsl(var(--border))",
          backgroundColor: "hsl(var(--card))",
        }}
      >
        <CardHeader
          title={t("USER_PAGE_ACCOUNT_INFO_TITLE")}
          sx={{ color: "hsl(var(--foreground))" }}
        />
        <CardContent>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            <Typography sx={{ color: "hsl(var(--foreground))" }}>
              <strong>{t("USER_PAGE_FULL_NAME_LABEL")}:</strong>{" "}
              {user.firstName} {user.lastName}
            </Typography>
            <Typography sx={{ color: "hsl(var(--foreground))" }}>
              <strong>{t("USER_PAGE_TITLE_LABEL")}:</strong> {user.title}
            </Typography>
            <Typography sx={{ color: "hsl(var(--foreground))" }}>
              <strong>{t("USER_PAGE_EMAIL_LABEL")}:</strong> {user.email}
            </Typography>
            <Typography sx={{ color: "hsl(var(--foreground))" }}>
              <strong>{t("USER_PAGE_PHONE_LABEL")}:</strong> {user.phoneNumber}
            </Typography>
            <Typography sx={{ color: "hsl(var(--foreground))" }}>
              <strong>{t("USER_PAGE_LOCATION_LABEL")}:</strong>{" "}
              {user.location?.name}
            </Typography>
            <Typography sx={{ color: "hsl(var(--foreground))" }}>
              <strong>{t("USER_PAGE_ENTITY_LABEL")}:</strong>{" "}
              {user.entity?.name}
            </Typography>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default UserPage;
